import { useState } from "react"
import { ArrowLeft, FileText, Rocket, Server, Users, Heart, Code, ExternalLink, Github, Construction } from "lucide-react"
import { Button } from "@/components/ui/button"

// Import markdown files as raw strings
import overviewContent from "@/docs/overview.md?raw"
import architectureContent from "@/docs/architecture.md?raw"
import communityContent from "@/docs/community.md?raw"
import contributingContent from "@/docs/contributing.md?raw"
import missionContent from "@/docs/mission.md?raw"
import roadmapContent from "@/docs/roadmap.md?raw"
import partnerContent from "@/docs/partner.md?raw"

const docsPages = [
    {
        id: "overview",
        title: "Overview",
        icon: FileText,
        content: overviewContent
    },
    {
        id: "architecture",
        title: "Architecture",
        icon: Server,
        content: architectureContent
    },
    {
        id: "community",
        title: "Community Strategy",
        icon: Users,
        content: communityContent
    },
    {
        id: "contributing",
        title: "Contributing",
        icon: Code,
        content: contributingContent
    },
    {
        id: "mission",
        title: "Our Mission",
        icon: Heart,
        content: missionContent
    },
    {
        id: "roadmap",
        title: "Roadmap",
        icon: Rocket,
        content: roadmapContent
    },
    {
        id: "partner",
        title: "Our Enablement Partner",
        icon: Heart,
        content: partnerContent
    }
]

export function DocsPage() {
    const [activePageId, setActivePageId] = useState("overview")
    const activePage = docsPages.find(p => p.id === activePageId)!

    return (
        <div className="min-h-screen bg-cream">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-cream/80 backdrop-blur-md border-b border-border/50">
                <nav className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <a href="/" className="flex items-center gap-2.5">
                                <div className="w-9 h-9 bg-forest rounded-lg flex items-center justify-center">
                                    <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-cream" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                                        <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" />
                                        <circle cx="8.5" cy="15.5" r="1.5" fill="currentColor" />
                                        <circle cx="15.5" cy="15.5" r="1.5" fill="currentColor" />
                                    </svg>
                                </div>
                                <span className="font-serif text-xl font-semibold text-foreground tracking-tight">
                                    Corkboard
                                </span>
                            </a>
                            <span className="text-muted-foreground">/</span>
                            <span className="text-sm font-medium text-foreground">Documentation</span>
                        </div>
                        <Button variant="ghost" size="sm" asChild>
                            <a href="/">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Home
                            </a>
                        </Button>
                    </div>
                </nav>
            </header>

            {/* Main Content */}
            <div className="pt-20 flex">
                {/* Sidebar */}
                <aside className="fixed left-0 top-20 bottom-0 w-64 bg-card border-r border-border overflow-y-auto">
                    <div className="p-4">
                        <div className="flex items-center gap-2 px-3 py-2 mb-4">
                            <Construction className="w-4 h-4 text-amber" />
                            <span className="text-xs font-medium text-amber">Work in Progress</span>
                        </div>
                        <nav className="space-y-1">
                            {docsPages.map((page) => {
                                const Icon = page.icon
                                const isActive = activePageId === page.id
                                return (
                                    <button
                                        key={page.id}
                                        onClick={() => setActivePageId(page.id)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                                            ? "bg-forest/10 text-forest"
                                            : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                                            }`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {page.title}
                                    </button>
                                )
                            })}
                        </nav>

                        <div className="mt-8 pt-6 border-t border-border">
                            <a
                                href="https://github.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                            >
                                <Github className="w-4 h-4" />
                                View on GitHub
                                <ExternalLink className="w-3 h-3 ml-auto" />
                            </a>
                        </div>
                    </div>
                </aside>

                {/* Content Area */}
                <main className="ml-64 flex-1 min-h-screen">
                    <div className="max-w-3xl mx-auto px-8 py-12">
                        <article className="prose prose-slate max-w-none">
                            <MarkdownRenderer key={activePageId} content={activePage.content} />
                        </article>
                    </div>
                </main>
            </div>
        </div>
    )
}

function MarkdownRenderer({ content }: { content: string }) {
    const lines = content.split('\n')

    return (
        <div className="space-y-4">
            {lines.map((line, index) => {
                // Skip empty lines
                if (!line.trim()) return null

                // H1
                if (line.startsWith('# ')) {
                    return (
                        <h1 key={index} className="font-serif text-3xl font-semibold text-foreground mb-6 mt-0">
                            {line.slice(2)}
                        </h1>
                    )
                }

                // H2
                if (line.startsWith('## ')) {
                    return (
                        <h2 key={index} className="font-serif text-2xl font-semibold text-foreground mt-10 mb-4">
                            {line.slice(3)}
                        </h2>
                    )
                }

                // H3
                if (line.startsWith('### ')) {
                    return (
                        <h3 key={index} className="font-semibold text-lg text-foreground mt-6 mb-3">
                            {line.slice(4)}
                        </h3>
                    )
                }

                // List items
                if (line.startsWith('- ')) {
                    const text = line.slice(2)
                    return (
                        <div key={index} className="flex items-start gap-2 text-muted-foreground ml-4">
                            <span className="text-forest mt-1.5">•</span>
                            <span>{text}</span>
                        </div>
                    )
                }

                // Regular paragraph
                return (
                    <p key={index} className="text-muted-foreground leading-relaxed">
                        {line}
                    </p>
                )
            })}
        </div>
    )
}
